import React, { useState, useEffect, useContext, useCallback } from 'react';
// import { Container, Table, Button, Row, Col, Form, InputGroup, Alert, Badge, Card } from 'react-bootstrap';
import { Container, Table, Button, Row, Col, Form, Alert, Badge, Card } from 'react-bootstrap';
// import { Link, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import api from '../../services/api';
import './Order.css';
import { useTranslation } from 'react-i18next';

const OrderList = () => {
  const { user, selectedCustomer } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { t } = useTranslation();

  const beginDate = new Date();
  beginDate.setDate(beginDate.getDate() - 7)
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + 7)
  const minDate = `${beginDate.getFullYear()}-${String(beginDate.getMonth() + 1).padStart(2, '0')}-${String(beginDate.getDate()).padStart(2, '0')}`;
  const maxDate = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

  // 查詢參數
  const [searchParams, setSearchParams] = useState({
    startDate: beginDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
    customerCode: '',
  });

  // const navigate = useNavigate();

  // 使用 useCallback 記憶 loadOrders 函數，避免無限遞迴
  const loadOrders = useCallback(async (customerCode) => {
    setLoading(true);
    setError('');

    try {
      // console.log('載入訂單，參數：', {
      //   customerCode,
      //   startDate: searchParams.startDate,
      //   endDate: searchParams.endDate
      // });

      if (!searchParams.startDate) {
        setError(t('msg_noStartDate'));
        return;
      }

      if (!searchParams.endDate) {
        setError(t('msg_noEndDate'));
        return;
      }

      let url = '/orders';
      const params = new URLSearchParams();

      // 添加日期範圍參數
      if (searchParams.startDate) {
        params.append('startDate', searchParams.startDate);
      }

      if (searchParams.endDate) {
        params.append('endDate', searchParams.endDate);
      }

      // 如果有客戶編號，添加到URL
      if (customerCode) {
        params.append('customerCode', customerCode);
      }

      // 如果有參數，添加到URL
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await api.get(url);
      // console.log('訂單API響應:', response.data);

      setOrders(await response.data.data || []);
    } catch (err) {
      console.error('載入訂單失敗:', err);
      console.error('錯誤詳情:', err.response?.data || err.message);
      setError('載入訂單失敗: ' + (err.response?.data?.message || '請稍後再試'));
      window.scrollTo(0, 0);
    } finally {
      setLoading(false);
    }
  }, [searchParams.startDate, searchParams.endDate]); // 只依賴於日期參數

  // 合併兩個 useEffect 為一個更簡潔的實現
  useEffect(() => {
    if (user) {
      const customerCode =
        // 如果是管理員且有指定客戶編號，使用該編號
        user.role === 0 && searchParams.customerCode ?
          searchParams.customerCode :
          // 如果有選擇的客戶，使用選擇的客戶編號  
          selectedCustomer ?
            selectedCustomer.cust_code :
            // 如果是客戶且沒有選擇客戶，使用空字符串（API會返回該用戶所有關聯客戶的訂單）  
            '';

      loadOrders(customerCode);
    }
  }, [
    user,
    selectedCustomer,
    searchParams.customerCode,
    searchParams.startDate,
    searchParams.endDate,
    loadOrders
  ]); // 包含所有必要的依賴項

  const handleSearch = (e) => {
    e.preventDefault();
    // 通過修改searchParams的值觸發useEffect重新載入訂單
    setSearchParams({ ...searchParams });
  };

  const handleClearSearch = () => {
    setSearchParams({
      startDate: minDate,
      endDate: maxDate,
      customerCode: '',
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-TW');
  };

  const getStatusBadge = (order) => {
    if (order.status === 'C') {
      return <Badge bg="danger">{t('cancelled')}</Badge>;
    } else if (order.status === 'P') {
      return <Badge bg="warning" text="dark">{t('processing')}</Badge>;
    } else if (order.status === 'D') {
      return <Badge bg="success">{t('completed')}</Badge>;
    } else if (order.status === 'O') {
      return <Badge bg="secondary">{t('created')}</Badge>;
    } else {
      return <Badge bg="secondary">{t('unknown')}</Badge>;
    }
  };

  return (
    <Container className="order-list-container">
      <Row className="mb-2 align-items-center">
        <Col xs={6}>
          <h2>{t('orderManagement')}</h2>
        </Col>
        <Col xs={6} className="text-end">
          <Button
            variant="outline-secondary"
            className="me-2"
            onClick={() => {
              const customerCode =
                user.role === 0 && searchParams.customerCode ?
                  searchParams.customerCode :
                  selectedCustomer ?
                    selectedCustomer.cust_code :
                    '';
              loadOrders(customerCode);
            }}
          >
            {t('refresh')}
          </Button>
          <Button variant="primary" as={Link} to="/orders/new">
            {t('placeOrder')}
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      {user?.role === 0 && (
        <Card className="mb-4">
          <Card.Body>
            <Form onSubmit={handleSearch}>
              <Row className="align-items-end">
                <Col md={user?.role === 0 ? 3 : 4}>
                  <Form.Group>
                    <Form.Label>{t('startDate')}</Form.Label>
                    <Form.Control
                      min={minDate}
                      max={maxDate}
                      type="date"
                      name='datePicker'
                      value={searchParams.startDate}
                      onChange={(e) => setSearchParams({ ...searchParams, startDate: e.target.value })}
                    />
                  </Form.Group>
                </Col>

                <Col md={user?.role === 0 ? 3 : 4}>
                  <Form.Group>
                    <Form.Label>{t('endDate')}</Form.Label>
                    <Form.Control
                      min={minDate}
                      max={maxDate}
                      type="date"
                      value={searchParams.endDate}
                      onChange={(e) => setSearchParams({ ...searchParams, endDate: e.target.value })}
                    />
                  </Form.Group>
                </Col>

                {user?.role === 0 && (
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>{t('customerNo')}</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder={t('enterCustomer')}
                        value={searchParams.customerCode}
                        onChange={(e) => setSearchParams({ ...searchParams, customerCode: e.target.value })}
                      />
                    </Form.Group>
                  </Col>
                )}

                <Col md={user?.role === 0 ? 3 : 4} className="mt-3 mt-md-0">
                  <Button type="submit" variant="primary" className="me-2">
                    {t('search')}
                  </Button>
                  <Button type="button" variant="outline-secondary" onClick={handleClearSearch}>
                    {t('clear')}
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>)}

      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('loading')}</span>
          </div>
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center my-5">
          <p>{t('noOrderFound')}</p>
          <Button variant="primary" as={Link} to="/orders/new">
            {t('placeOrder')}
          </Button>
        </div>
      ) : (
        <div className="table-responsive">
          <Table hover className="order-table">
            <thead>
              <tr style={{ fontSize: 'clamp(1rem, 1vw, 1.05rem)' }}>
                <th></th>
                <th style={{ minWidth: '50px', padding: '3px' }}>{t('orderNo')}</th>
                <th style={{ padding: '3px' }}>{t('orderStatus')}</th>
                <th style={{ padding: '3px' }}>{t('deliveryDate')}</th>
                {user.role === 0 ? <th style={{ padding: '3px' }}>{t('customer')}</th> : <></>}
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr style={{ fontSize: 'clamp(1rem, 1vw, 1rem)' }} key={order.order_id}>
                  <td style={{ padding: '4px' }}>
                    <Button
                      variant="link"
                      className="p-0 text-primary"
                      style={{ textDecoration: 'none' }}
                      as={Link}
                      to={`/orders/${order.order_id}`}
                    >
                      {/* 查看👁️ */}🔍
                    </Button>
                  </td>
                  <td style={{ padding: '3px' }}>{order.order_id.slice(-4)}</td>
                  <td style={{ padding: '3px' }}>{getStatusBadge(order)}</td>
                  <td style={{ padding: '3px' }}>{formatDate(order.delivery_date)}</td>
                  {user.role === 0 ? <td style={{ padding: '3px' }}>{order.cust_name} ({order.cust_code})</td> : <></>}
                </tr>

              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Container>
  );
};

export default OrderList;